import React, { PropsWithChildren, useContext } from 'react';
import TemplateEditorContext from '../contexts/TemplateEditorContext';
import { useUpdateQuestionConfig } from '../hooks/questionConfig';
import { useCreateQuestion } from '../hooks/question';
import QuestionGroupContext from './QuestionGroupContext';
import { Question } from '@stormwater-software/lib-inspection-templates';
import { questionApi } from '../utils/api/question';

export type InspectionQuestionContextProps = {
  addQuestion: (question: any) => Promise<any>;
  updateQuestionConfig: (question: any) => Promise<any>;
  updateQuestion: (question: Question & { id: number }) => Promise<any>;
  deleteQuestion: (questionId: number) => Promise<any>;
  getIsUsedTemplate: (questionId: number) => Promise<boolean>;
};

const throwError = () => {
  throw new Error('InspectionQuestionProvider is not loaded.');
};

const Context = React.createContext<InspectionQuestionContextProps>({
  addQuestion: throwError,
  updateQuestionConfig: throwError,
  updateQuestion: throwError,
  deleteQuestion: throwError,
  getIsUsedTemplate: throwError,
});

export const InspectionQuestionProvider: React.FC<
  PropsWithChildren<{ initialValue: { groupId: number } }>
> = ({ initialValue, children }) => {
  const questionGroupContext = useContext(QuestionGroupContext);
  const templateEditorContext = useContext(TemplateEditorContext);
  const newQuestionQuery = useCreateQuestion(initialValue.groupId);
  const updateQuestionConfigQuery = useUpdateQuestionConfig();

  const updateQuestionConfig = async (question: any) => {
    const configValues: Array<any> = [];
    const questionTypeId = parseInt(question.question_type_id);

    templateEditorContext.questionConfigData
      .filter((field: any) => field.questionTypeId === questionTypeId)
      .forEach((field: any) => {
        if (field.fieldName === 'options') {
          if ('options' in question) {
            configValues.push({
              value: JSON.stringify(question.options),
              questionId: question.id,
              questionFieldId: field.id,
            });
          }

          return;
        }

        if (
          field.fieldName in question &&
          question[field.fieldName] !== undefined
        ) {
          configValues.push({
            value: question[field.fieldName],
            questionId: question.id,
            questionFieldId: field.id,
          });
        }
      });
    if (configValues.length === 0) {
      return await questionGroupContext.refetchQuestionGroup();
    }

    return await updateQuestionConfigQuery
      .mutateAsync(configValues)
      .then(async () => {
        await questionGroupContext.refetchQuestionGroup();
      });
  };

  const addQuestion = async (question: any) => {
    await newQuestionQuery
      .mutateAsync(question)
      .then((data) => updateQuestionConfig({ ...question, id: data.id }));
  };

  const updateQuestion = async (question: Question & { id: number }) => {
    return questionApi.update(question.id, question).then(() => {
      return updateQuestionConfig(question);
    });
  };

  const deleteQuestion = async (questionId: number) => {
    return questionApi.delete(questionId).then(() => {
      return questionGroupContext.refetchQuestionGroup();
    });
  };

  const getIsUsedTemplate = async (questionId: number) => {
    return questionApi.getIsUsedTemplate(questionId);
  };

  return (
    <Context.Provider
      value={{
        addQuestion,
        updateQuestionConfig,
        updateQuestion,
        deleteQuestion,
        getIsUsedTemplate,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default Context;
